/**
 * ARR/DEP and M/N pills — the single source of truth for how a session's
 * operation mode and shift type are shown as small colored tags. ModePill
 * originated in the counter grid's corner cell (see RosterGrid.tsx); both
 * are also used per-row in the past-sessions table (CoverPage.tsx) so every
 * place renders them identically.
 */

import { modeColors } from '@/utils/modeColors';
import { IS_FINE_POINTER, sc } from '@/utils/uiScale';

interface ModePillProps {
  /** Undefined/null renders nothing — used where a session has no saved state yet. */
  mode?: string | null;
  /** Shrinks the pill for tight spaces (e.g. stacked above a name). Defaults to false. */
  compact?: boolean;
  /** Grow with the desktop UI scale (and start larger on a mouse device) — for the counter grid's corner cell. Phones keep the small size. */
  scaled?: boolean;
}

export function ModePill({ mode, compact = false, scaled = false }: ModePillProps) {
  if (!mode) return null;
  const isDeparture = mode === 'departure';
  return (
    <span
      title={isDeparture ? 'Departure' : 'Arrival'}
      style={{
        display: 'inline-block',
        fontSize: scaled && IS_FINE_POINTER ? sc(11) : scaled ? sc(7) : compact ? 8 : 7, fontWeight: 800, letterSpacing: '0.02em', lineHeight: 1, flexShrink: 0,
        padding: scaled && IS_FINE_POINTER ? `${sc(3)}px ${sc(5)}px` : compact ? '2px 4px' : '2px 3px 1px', borderRadius: 3,
        background: modeColors(mode).pill,
        color: 'white',
      }}
    >
      {isDeparture ? 'DEP' : 'ARR'}
    </span>
  );
}

interface ShiftPillProps {
  /** Undefined/null renders nothing — used where a session has no saved state yet. */
  shiftType?: string | null;
  compact?: boolean;
}

/**
 * M/N pill for a session's shift type. Deliberately neutral grayscale (light
 * for morning/daytime, dark for night) rather than a hue — every other color
 * in the app's status/identity palette is already spoken for (see
 * the OT colours in utils/otColors.ts), and a light/dark pair reads as day-vs-
 * night on its own without borrowing a color that means something else.
 */
export function ShiftPill({ shiftType, compact = false }: ShiftPillProps) {
  if (!shiftType) return null;
  const isNight = shiftType === 'night';
  return (
    <span
      title={isNight ? 'Night shift' : 'Morning shift'}
      style={{
        display: 'inline-block',
        fontSize: compact ? 8 : 7, fontWeight: 800, letterSpacing: '0.02em', lineHeight: 1, flexShrink: 0,
        padding: compact ? '2px 4px' : '2px 3px 1px', borderRadius: 3,
        background: isNight ? '#334155' : '#e2e8f0',
        color: isNight ? 'white' : '#334155',
      }}
    >
      {isNight ? 'N' : 'M'}
    </span>
  );
}
